import { useState, useEffect } from 'react';
import { Bell, RefreshCw, Upload, Plus, Wifi, WifiOff } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../lib/api';
import { useWebSocket } from '../contexts/WebSocketContext';

export const Header = ({ title, subtitle, onRefresh, lastUpdated, onUpload, onAddDeal }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ws = useWebSocket();

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Real-time notification updates
  useEffect(() => {
    if (!ws) return;
    return ws.subscribe('notification_created', () => {
      loadNotifications();
    });
  }, [ws]);

  const loadNotifications = async () => {
    try {
      const res = await getNotifications(false);
      setNotifications(res.data.slice(0, 10));
      setUnreadCount(res.data.filter(n => !n.read).length);
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark notification read', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <header className="bg-white border-b border-slate-200 px-6 py-4" data-testid="header">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* WebSocket connection indicator */}
          <div className="flex items-center gap-1.5" data-testid="ws-status" title={ws?.connected ? 'Live updates active' : 'Connecting...'}>
            {ws?.connected ? (
              <Wifi size={14} className="text-emerald-500" />
            ) : (
              <WifiOff size={14} className="text-slate-300" />
            )}
            <span className={`text-[10px] uppercase tracking-wider font-semibold ${ws?.connected ? 'text-emerald-500' : 'text-slate-300'}`}>
              {ws?.connected ? 'Live' : '...'}
            </span>
          </div>

          {lastUpdated && (
            <span className="text-xs text-slate-400 font-mono">
              Last updated: {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          )}

          {onUpload && (
            <Button
              variant="outline"
              size="sm"
              onClick={onUpload}
              data-testid="upload-csv-btn"
              className="uppercase tracking-wide text-xs font-semibold"
            >
              <Upload size={16} className="mr-2" />
              Upload CSV
            </Button>
          )}

          {onAddDeal && (
            <Button
              size="sm"
              onClick={onAddDeal}
              data-testid="add-deal-header-btn"
              className="uppercase tracking-wide text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Plus size={16} className="mr-2" />
              Add Deal
            </Button>
          )}

          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              data-testid="refresh-btn"
              className="uppercase tracking-wide text-xs font-semibold"
            >
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="relative"
                data-testid="notifications-btn"
              >
                <Bell size={16} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-slate-400">
                  No notifications
                </div>
              ) : (
                notifications.map((notif) => (
                  <DropdownMenuItem
                    key={notif.id}
                    onClick={() => handleMarkRead(notif.id)}
                    className={`flex flex-col items-start gap-1 px-3 py-2 cursor-pointer ${!notif.read ? 'bg-blue-50' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 ${
                        notif.type === 'urgent' ? 'bg-red-500' :
                        notif.type === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                      }`} />
                      <span className="text-sm font-medium">{notif.title}</span>
                    </div>
                    <span className="text-xs text-slate-500">{notif.message}</span>
                    <span className="text-xs text-slate-400">{formatTime(notif.created_at)}</span>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
